import type { FieldDefinition, FieldGroup } from '@/config/formConfig'
import Input from '@/components/ui/Input'

interface DynamicFieldGroupProps {
  group: FieldGroup
  values: Record<string, any>
  onChange: (name: string, value: any) => void
  customLabels?: Record<string, string> // Allow overriding labels
}

export default function DynamicFieldGroup({ group, values, onChange, customLabels = {} }: DynamicFieldGroupProps) {
  const renderField = (field: FieldDefinition) => {
    const label = customLabels[field.name] || field.label
    const value = values[field.name] || ''

    switch (field.type) {
      case 'select':
        return (
          <select
            key={field.name}
            name={field.name}
            value={value}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
            className="input"
          >
            <option value="">Select {label}</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )

      case 'textarea':
        return (
          <textarea
            key={field.name}
            name={field.name}
            placeholder={field.placeholder || label}
            value={value}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
            rows={4}
            className="input"
            style={{ resize: 'vertical', minHeight: '100px' }}
          />
        )

      case 'date':
        return (
          <Input
            key={field.name}
            name={field.name}
            type="date"
            value={value}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
          />
        )

      case 'number':
        return (
          <Input
            key={field.name}
            name={field.name}
            type="number"
            placeholder={field.placeholder || label}
            value={value}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
          />
        )

      default: // text
        return (
          <Input
            key={field.name}
            name={field.name}
            type="text"
            placeholder={field.placeholder || label}
            value={value}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
          />
        )
    }
  }

  return (
    <div className="glass-card" style={{
      padding: 'var(--spacing-lg)',
      borderRadius: 'var(--radius-lg)',
      marginBottom: 'var(--spacing-lg)'
    }}>
      {group.title && (
        <div className="flex items-center mb-6" style={{ gap: 'var(--spacing-md)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
            background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)'
          }}>
            <svg className="w-4 h-4" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {group.icon === 'user' && (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              )}
              {group.icon === 'users' && (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              )}
              {group.icon === 'calendar' && (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              )}
              {!group.icon && (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              )}
            </svg>
          </div>
          <h4 className="text-lg font-semibold gradient-text">{group.title}</h4>
        </div>
      )}

      <div className="space-y-4">
        {group.fields.map(field => (
          <div key={field.name}>
            {field.type !== 'select' && field.type !== 'textarea' && (
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                {customLabels[field.name] || field.label}
                {field.required && <span style={{ color: 'var(--accent-danger)' }}> *</span>}
              </label>
            )}
            {renderField(field)}
          </div>
        ))}
      </div>
    </div>
  )
}
